import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { getHash } from '../helpers/hash'
import { getAuthQuery } from '../chat/queries'

const WebSocketContext = createContext()

export const WebSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null)
  const [isConnected, setIsConnected] = useState(false)
  const [responses, setResponses] = useState({})
  const queueRef = useRef([])
  const tokenRef = useRef(null)

  // Callbacks for subscriptions
  const responseCallbacksRef = useRef(new Set())
  const connectRef = useRef(null)

  const connect = useCallback((token) => {
    tokenRef.current = token

    if (socket) {
      socket.close()
    }

    const newSocket = new WebSocket('/api/message')

    newSocket.onopen = () => {
      console.log('Chat connected')
      setIsConnected(true)
      const authHash = getHash("auth")

      // Send authentication
      newSocket.send(JSON.stringify({ ...getAuthQuery(token), id: authHash }))

      // Process queued messages
      while (queueRef.current.length > 0) {
        console.log(`Message queue: ${queueRef.current.length} items`)
        const item = queueRef.current.shift()
        setTimeout(() => {
          if (newSocket.readyState === WebSocket.OPEN) {
            newSocket.send(JSON.stringify({ ...item.message, id: item.uniqueId }))
          } else {
            queueRef.current.push(item)
          }
        }, 5000)
      }
    }

    newSocket.onclose = (e) => {
      setIsConnected(false)
      if (!e.wasClean) {
        console.log('Chat connection error, reconnecting...', e)
        setTimeout(() => {
          if (connectRef.current) {
            connectRef.current(tokenRef.current)
          }
        }, 1000)
      }
    }

    newSocket.onerror = (e) => {
      console.error('WebSocket error:', e)
    }

    newSocket.onmessage = (res) => {
      if (res.data) {
        try {
          const data = JSON.parse(res.data)

          // Notify response subscribers
          responseCallbacksRef.current.forEach(callback => {
            callback(data)
          })

          // Store response by ID for useSocket hook
          if (data.id) {
            setResponses(prev => ({
              ...prev,
              [data.id]: data.result || data
            }))
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }
    }

    setSocket(newSocket)

    return () => {
      newSocket.close()
    }
  }, [socket])

  // Update the ref when connect changes
  useEffect(() => {
    connectRef.current = connect
  }, [connect])

  const send = useCallback((message, uniqueId) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      const sendData = JSON.stringify({ ...message, id: uniqueId })
      socket.send(sendData)
    } else {
      if (message.method !== 'authenticate') {
        console.log('Chat is not connected, queueing outgoing message...')
        queueRef.current.push({ message, uniqueId })
      }
    }
  }, [socket])

  const subscribeToResponses = useCallback((callback) => {
    responseCallbacksRef.current.add(callback)

    // Return unsubscribe function
    return () => {
      responseCallbacksRef.current.delete(callback)
    }
  }, [])

  const getResponse = useCallback((hash) => {
    return responses[hash]
  }, [responses])

  const value = {
    socket,
    isConnected,
    connect,
    send,
    subscribeToResponses,
    getResponse,
    responses
  }

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocket = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
